import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { KeychainAccess, SecureStorage } from "@aparajita/capacitor-secure-storage";

/**
 * tokenStore — the ONE place the session tokens are read from and written to.
 *
 * WHY THIS EXISTS (kernel/blaze parallel run, Slice A — decision D28)
 * Token access was spread across ~28 call sites in 6 files. Two consequences:
 *
 *  1. Six of those sites used raw `fetch` and therefore had NO refresh path at all
 *     (user.ts, postsService.ts, fileService.ts, AuthenticatedImage.tsx ×3). That is
 *     nearly harmless today because a blaze token lasts `JWT_EXPIRY = 1d`; after the
 *     cutover a Cognito ACCESS token lasts **15 minutes**, so those sites go from
 *     "rarely stale" to "stale constantly". Latent today, broken at cutover.
 *  2. Native secure storage (HIPAA #6 / item P1, decision D15). Because every token read
 *     and write goes through here, moving the backing store was a one-file change.
 *
 * NATIVE STORAGE (F-603, URS_3.4 / ACDPA-19 AC1). On iOS and Android the tokens live in the
 * OS secure store via `@aparajita/capacitor-secure-storage`: the iOS Keychain, and on
 * Android values encrypted with a key held in the Android Keystore. They are NOT in
 * `@capacitor/preferences` (plain UserDefaults / SharedPreferences) any more -- HIPAA #6
 * forbids that. Builds before F-603 did write them to Preferences, so the first token
 * operation after an upgrade moves them across once and deletes the Preferences copies
 * (`migrateLegacyTokens`). `clearTokens` still removes the Preferences keys too, so a
 * sign-out can never leave a plaintext copy behind even if that migration failed.
 *
 * ⚠️ SCOPE — two different things are called "token" in this codebase:
 *   * `authToken` / `refreshToken`  — the SESSION tokens. This module owns them.
 *   * `token`                       — the short-lived OTP *session* handle passed between
 *                                     login-start and login-verify (services/auth.ts,
 *                                     VerifyLogin.tsx). A different thing with an
 *                                     unfortunately similar name. NOT owned here; do not
 *                                     fold it in.
 *
 * ⚠️ WEB vs NATIVE (ADR-0009 / D6). On web the session lives in an HttpOnly cookie that
 * JavaScript cannot read -- that is the point of the flag, not a limitation to work
 * around. So on web every getter returns null and callers must send `withCredentials`
 * instead of an Authorization header. Do not "fix" this by moving web tokens somewhere
 * readable.
 */

const ACCESS_KEY = "authToken";
const REFRESH_KEY = "refreshToken";
const TOKEN_KEYS = [ACCESS_KEY, REFRESH_KEY];

/**
 * iOS keychain accessibility. `afterFirstUnlockThisDeviceOnly` keeps the token readable by
 * work the app does while the phone is locked (as Preferences was), and never lets it travel
 * to another device through a backup restore. A no-op on Android.
 */
const KEYCHAIN_ACCESS = KeychainAccess.afterFirstUnlockThisDeviceOnly;

let nativeStoreReady: Promise<void> | null = null;

/**
 * Preferences key that says "this install has already run the secure store's first launch"
 * (built on default D-346). Preferences is wiped when the app is uninstalled; the iOS
 * Keychain is not. So no marker means a fresh install, and any token in the secure store is
 * left over from a previous install (or a previous user of the phone) and must not be sent.
 * Sign-out's `Preferences.clear()` also wipes it, which is why `setTokens` writes it again.
 */
const INSTALL_MARKER_KEY = "tokenStoreInstalled";

/** True once this launch has confirmed the secure store holds nothing from a previous install. */
let installChecked = false;

/**
 * Whether this native shell actually contains the NATIVE secure-storage plugin. A shell built
 * without `npx cap sync` does not; calling the plugin there never settles (its JS fallback
 * recurses until the app runs out of memory), so every native path checks this first and,
 * when it is false, keeps the pre-F-603 behaviour: tokens in Preferences, nothing moved,
 * nothing deleted.
 *
 * Detected from the native bridge's `PluginHeaders` (the list of native plugins compiled into
 * the shell; the bridge sets it on `window.Capacitor`, which is the `Capacitor` object here).
 * NOT `Capacitor.isPluginAvailable`: the plugin registers JS classes for ios and android, and
 * Capacitor 8 counts those as "available" whether or not the native code is there (R-1090).
 */
const secureStoreAvailable = (): boolean =>
  Capacitor.isNativePlatform() &&
  !!(Capacitor as { PluginHeaders?: readonly { name: string }[] }).PluginHeaders?.some(
    (header) => header.name === "SecureStorage",
  );

/**
 * First launch after an install (no marker): drop whatever the secure store still holds
 * from a previous install. The marker is written after the clear and BEFORE the legacy
 * migration, so a migration that fails half-way and is retried never re-clears a token it
 * has already moved. An upgrade from a Preferences build also has no marker, but its
 * tokens are still in Preferences at this point, so the clear touches nothing of theirs
 * and the migration below carries them across.
 */
const clearTokensLeftByPreviousInstall = async (): Promise<void> => {
  const { value } = await Preferences.get({ key: INSTALL_MARKER_KEY });
  if (!value) {
    for (const key of TOKEN_KEYS) {
      await SecureStorage.removeItem(key);
    }
    await Preferences.set({ key: INSTALL_MARKER_KEY, value: "1" });
  }
  installChecked = true;
};

/**
 * One-time move of pre-F-603 tokens from Preferences into the secure store. A token already
 * in the secure store is newer than the Preferences copy, so it wins and the copy is just
 * deleted. A Preferences copy is only deleted after the secure write succeeded.
 */
const migrateLegacyTokens = async (): Promise<void> => {
  await SecureStorage.setDefaultKeychainAccess(KEYCHAIN_ACCESS);
  await clearTokensLeftByPreviousInstall();
  for (const key of TOKEN_KEYS) {
    const { value } = await Preferences.get({ key });
    if (value === null || value === undefined) {
      continue;
    }
    const existing = await SecureStorage.getItem(key);
    if (existing === null && value) {
      await SecureStorage.setItem(key, value);
    }
    await Preferences.remove({ key });
  }
};

/**
 * Runs the migration once per app launch, before any native token read or write. If it
 * fails (no token value is logged), the next token operation tries again; reads then come
 * from the secure store only, so the worst case is a sign-in prompt, never a plaintext read.
 */
const ensureNativeStore = (): Promise<void> => {
  if (!nativeStoreReady) {
    nativeStoreReady = migrateLegacyTokens().catch((error: unknown) => {
      nativeStoreReady = null;
      console.warn(
        "tokenStore: moving tokens to secure storage failed; will retry",
        error instanceof Error ? error.message : "unknown error",
      );
    });
  }
  return nativeStoreReady;
};

/** Read one token: secure store, or Preferences on a shell without the plugin. */
const readToken = async (key: string): Promise<string | null> => {
  if (!secureStoreAvailable()) {
    const { value } = await Preferences.get({ key });
    return value || null;
  }
  await ensureNativeStore();
  if (!installChecked) {
    // The first-launch clear failed (it is retried on the next call). Until it succeeds,
    // whatever is in the secure store may belong to a previous install: send nothing.
    return null;
  }
  return (await SecureStorage.getItem(key)) || null;
};

/** Web keeps the session in an HttpOnly cookie; there is nothing for JS to read. */
export const isWebPlatform = (): boolean => Capacitor.getPlatform() === "web";

/** The bearer token for native requests. Always null on web -- by design. */
export const getAccessToken = async (): Promise<string | null> => {
  if (isWebPlatform()) {
    return null;
  }
  return readToken(ACCESS_KEY);
};

/** The refresh token for native. Always null on web (the cookie carries it). */
export const getRefreshToken = async (): Promise<string | null> => {
  if (isWebPlatform()) {
    return null;
  }
  return readToken(REFRESH_KEY);
};

/**
 * Persist a new session. No-op on web -- the server's Set-Cookie already did it, and
 * writing a copy anywhere JS can read would defeat the HttpOnly cookie.
 */
export const setTokens = async (
  accessToken: string,
  refreshToken?: string | null,
): Promise<void> => {
  if (isWebPlatform()) {
    return;
  }
  if (!secureStoreAvailable()) {
    await Preferences.set({ key: ACCESS_KEY, value: accessToken });
    if (refreshToken) {
      await Preferences.set({ key: REFRESH_KEY, value: refreshToken });
    }
    return;
  }
  await ensureNativeStore();
  await SecureStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) {
    await SecureStorage.setItem(REFRESH_KEY, refreshToken);
  }
  // Sign-out's Preferences.clear() removed the install marker; without it the next launch
  // would treat this new session as left over from a previous install and drop it.
  await Preferences.set({ key: INSTALL_MARKER_KEY, value: "1" });
};

/**
 * Drop the session. Safe to call on web (no-op) and when already empty. On native it clears
 * the secure store AND any legacy Preferences copy, so sign-out leaves no token anywhere.
 */
export const clearTokens = async (): Promise<void> => {
  if (isWebPlatform()) {
    return;
  }
  if (!secureStoreAvailable()) {
    for (const key of TOKEN_KEYS) {
      await Preferences.remove({ key });
    }
    return;
  }
  await ensureNativeStore();
  // Every delete is attempted even if an earlier one throws, so a failing secure store can
  // never leave the Preferences copy behind. The first failure is still reported.
  const results = await Promise.allSettled(
    TOKEN_KEYS.flatMap((key) => [
      Promise.resolve().then(() => SecureStorage.removeItem(key)),
      Promise.resolve().then(() => Preferences.remove({ key })),
    ]),
  );
  const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
  if (failed) {
    throw failed.reason;
  }
};

/**
 * `Authorization` header for a native request, or `{}` on web.
 *
 * Use this instead of building the header by hand -- it is what keeps the web/native
 * branch in ONE place rather than repeated at every call site (D6's transport boundary).
 * Pair it with `credentials: "include"` / `withCredentials` on web.
 */
export const authHeader = async (): Promise<Record<string, string>> => {
  const token = await getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
